import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Pagination, Spin, Table, Typography } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import { ApiClientContext } from "../../../App";
import { detailedColumns } from "./columns";
import { formatMinutes, toApiStartDate, toApiEndDate } from "../utils";
import { useTableBodyHeight } from "./useTableBodyHeight";
import {
  DetailedReportEntry,
  DetailedReportEntriesDTO,
  DetailedReportHeaderDTO,
  DetailedReportPayload,
} from "../types";

const PAGE_SIZE = 15;

const Detailed = () => {
  const location = useLocation();
  const { apiClient } = useContext(ApiClientContext);

  const params = new URLSearchParams(location.search);
  const startDate = params.get("startDate") ?? "";
  const endDate = params.get("endDate") ?? "";
  const projectIds = params.get("projectIds") ?? "";
  const employeeIds = params.get("employeeIds") ?? "";

  const [header, setHeader] = useState<DetailedReportHeaderDTO | null>(null);
  const [entries, setEntries] = useState<DetailedReportEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [orderBy, setOrderBy] = useState<string | undefined>(undefined);
  const [desc, setDesc] = useState(true);

  const [isLoadingHeader, setIsLoadingHeader] = useState(false);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [entriesError, setEntriesError] = useState<string | null>(null);

  const buildPayload = (): DetailedReportPayload => ({
    startDate: toApiStartDate(startDate),
    endDate: toApiEndDate(endDate),
    projectIds: projectIds ? projectIds.split(",") : [],
    employeeIds: employeeIds ? employeeIds.split(",") : [],
  });

  const fetchHeader = async () => {
    try {
      setIsLoadingHeader(true);
      setHeaderError(null);
      const { data } = await apiClient.post<DetailedReportHeaderDTO>(
        "/api/time-tracking/reports/detailed",
        buildPayload(),
      );
      setHeader(data);
    } catch (err: any) {
      setHeaderError(
        err?.response?.data?.message ?? "Failed to load report header.",
      );
    } finally {
      setIsLoadingHeader(false);
    }
  };

  const fetchEntries = async (page: number, ob?: string, d?: boolean) => {
    try {
      setIsLoadingEntries(true);
      setEntriesError(null);
      const sortDesc = d ?? desc;
      const sortBy = ob ?? orderBy;
      const qs = new URLSearchParams({
        Size: String(PAGE_SIZE),
        Page: String(page),
        Desc: String(sortDesc),
        ...(sortBy ? { OrderBy: sortBy } : {}),
      });
      const { data } = await apiClient.post<DetailedReportEntriesDTO>(
        `/api/time-tracking/reports/detailed/entries?${qs.toString()}`,
        buildPayload(),
      );
      setEntries(data.items);
      setTotalCount(data.totalCount);
    } catch (err: any) {
      setEntriesError(
        err?.response?.data?.message ?? "Failed to load report entries.",
      );
    } finally {
      setIsLoadingEntries(false);
    }
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchHeader();
    setPageCount(0);
  }, [startDate, endDate, projectIds, employeeIds]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchEntries(pageCount, orderBy, desc);
  }, [startDate, endDate, projectIds, employeeIds, pageCount, orderBy, desc]);

  const { tableRef, paginationRef, bodyHeight } = useTableBodyHeight();

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter:
      | SorterResult<DetailedReportEntry>
      | SorterResult<DetailedReportEntry>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const newOrderBy = s.order ? (s.columnKey as string) : undefined;
    const newDesc = s.order === "descend" || !s.order;
    setOrderBy(newOrderBy);
    setDesc(newDesc);
    setPageCount(0);
  };

  return (
    <div style={{ padding: "0px 8px " }}>
      {entriesError && (
        <Alert
          type="error"
          message={entriesError}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <div ref={tableRef}>
        <Spin spinning={isLoadingEntries}>
          <Table<DetailedReportEntry>
            dataSource={entries}
            columns={detailedColumns}
            rowKey="sessionId"
            pagination={false}
            onChange={handleTableChange}
            size="middle"
            style={{ borderRadius: 8 }}
            rowClassName={() => "detailed-report-row"}
            scroll={
              entries.length > 0
                ? { x: "max-content", y: bodyHeight }
                : undefined
            }
            title={() => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "2px 0",
                }}
              >
                <Typography.Text style={{ color: "#8c8c8c", fontSize: 13 }}>
                  Total:
                </Typography.Text>
                {isLoadingHeader ? (
                  <span style={{ color: "#8c8c8c", fontSize: 13 }}>—</span>
                ) : headerError ? (
                  <span style={{ color: "#ff4d4f", fontSize: 13 }}>
                    {headerError}
                  </span>
                ) : (
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {header ? formatMinutes(header.totalDurationMinutes) : "—"}
                  </Typography.Text>
                )}
              </div>
            )}
          />
        </Spin>
      </div>

      <div
        ref={paginationRef}
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 16,
          height: 56,
          alignItems: "center",
        }}
      >
        {totalCount > PAGE_SIZE && (
          <Pagination
            current={pageCount + 1}
            pageSize={PAGE_SIZE}
            total={totalCount}
            showSizeChanger={false}
            onChange={(p) => setPageCount(p - 1)}
            size="default"
            style={{ fontSize: 15 }}
          />
        )}
      </div>
    </div>
  );
};

export default Detailed;
